import React, { useState } from "react";
import { X, Table, FileText } from "lucide-react";

export default function CreateTableModal({ isOpen, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    table_name: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors = {};

    if (!formData.table_name.trim()) {
      newErrors.table_name = "테이블 이름은 필수입니다.";
    } else if (formData.table_name.length > 255) {
      newErrors.table_name = "테이블 이름은 255자를 초과할 수 없습니다.";
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "설명은 1000자를 초과할 수 없습니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ table_name: "", description: "" });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Table className="w-6 h-6" />
              <h2 className="text-xl font-bold">새 테이블 생성</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-5">
            {/* Table Name */}
            <div>
              <label
                htmlFor="table_name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                테이블 이름 <span className="text-red-500">*</span>
              </label>
              <input
                id="table_name"
                type="text"
                value={formData.table_name}
                onChange={(e) =>
                  setFormData({ ...formData, table_name: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.table_name ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="예: 2024년 매출 관리"
                disabled={submitting}
              />
              {errors.table_name && (
                <p className="mt-1 text-sm text-red-600">{errors.table_name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                설명
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="테이블에 대한 설명을 입력하세요"
                rows={4}
                disabled={submitting}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.description.length}/1000
              </p>
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">테이블 생성 후</p>
                <ul className="space-y-1 text-xs">
                  <li>• 기본 컬럼(ID, 생성일, 수정일)이 자동으로 추가됩니다</li>
                  <li>• 필요한 컬럼을 추가하여 데이터 구조를 정의하세요</li>
                  <li>• 다른 사용자와 테이블을 공유할 수 있습니다</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              취소
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? "생성 중..." : "생성"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
